import React, {memo, useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {useHistory} from 'react-router-dom';
import useNavigation from '@/hooks/useNavigation';
import NavMenu from '@/layout/Menu/index';
import NavigationGroup from '@/layout/NavigationGroup/index';
import forEach from 'lodash/forEach';
import {useLocation} from 'react-router-dom';
import useCurrentSpace from '@/modules/spacesSetting/hooks/useCurrentSpace';
import { useTranslation } from 'react-i18next';
import {useInternationalization} from '@/contexts/InternationalizationContext';
import filter from 'lodash/filter';

function findActiveKeys(menuList, pathname) {
  let activeKeys = [];
  forEach(menuList, (item, index) => {
    if (item?.link && pathname.startsWith(item?.link)) {
      activeKeys.push(`link=>${item?.link}`);
    }
    if (item?.menu && item?.title) {
      const activeSubKeys = findActiveKeys(item?.menu, pathname);
      if (activeSubKeys.length) {
        activeKeys = [
          `${index}-${item.title}`,
          ...activeKeys,
          ...activeSubKeys
        ];
      }
    }
  });
  return activeKeys;
}

const NavigationMenu = ({theme, mode, disabled, collapsed}) => {
  const navigation = useNavigation();
  const {push} = useHistory();
  const { t } = useTranslation('navigation');
  const {locale} = useInternationalization();
  const {pathname} = useLocation();

  const prefixKey = useMemo(() => `${pathname}-${locale}`, [locale, pathname]);

  const {data: space} = useCurrentSpace();

  const spaceNavigation = useMemo(() => {
    if (space?.isRootSpace) {
      let rootNavigation = filter(navigation, nav => nav.title !== t('main'));

      rootNavigation = rootNavigation.map(root =>
        root.title === t('admin') ? { ...root, menu: root.menu.map(adm =>
          adm.title === t('setting') ? { ...adm, menu: adm.menu.filter(adM =>
            adM.link === '/admin/setting/general' || adM.link === '/admin/setting/auth'
          )} : adm
        )} : root);

      rootNavigation = rootNavigation.map(root =>
        root.title === t('admin') ? { ...root, menu: root.menu.map(adm =>
          adm.title === t('security') ? { ...adm, menu: [...adm.menu, {
            title: t('/admin/spaces'),
            link: '/admin/spaces',
            permissions: ['USER.READ']
          }]} : adm) } : root);

      return rootNavigation;
    }

    return navigation;
  }, [navigation, space, t]);

  const openKeys = useMemo(() => findActiveKeys(spaceNavigation, pathname), [spaceNavigation, pathname]);

  const handleClick = useCallback(({link}) => {
    push(link);
  }, [push]);

  const baseMenuProps = useMemo(() => ({
    defaultSelectedKeys: openKeys,
    selectable: false
  }), [openKeys]);

  const menuProps = useMemo(() => ({
    ...baseMenuProps,
    openKeys,
  }), [baseMenuProps, openKeys]);

  const groupMenuProps = useMemo(() => {
    const menuProps = {...baseMenuProps};
    if (!collapsed){
      menuProps.defaultOpenKeys = openKeys;
    }
    return menuProps;
  }, [baseMenuProps, collapsed, openKeys]);

  if (Array.isArray(spaceNavigation)) {
    if (mode === 'horizontal')
      return <NavMenu
        key={`${prefixKey}-horizontal`}
        subMenuProps={{disabled}}
        mode={mode} menu={spaceNavigation} theme={theme} onClick={handleClick}
        menuProps={menuProps}/>;

    return spaceNavigation.map((nav, key) => (
      <NavigationGroup
        subMenuProps={{disabled}}
        mode={mode} key={`${prefixKey}-${key}`} index={key} {...nav} theme={theme} onClick={handleClick}
        menuProps={groupMenuProps}/>
    ));
  }

  return (
    <NavMenu
      key={`${prefixKey}-vertical`}
      subMenuProps={{disabled}}
      mode={mode} menu={spaceNavigation?.menu} theme={theme} onClick={handleClick}
      menuProps={baseMenuProps}/>
  );
};

NavigationMenu.propTypes = {
  mode: PropTypes.string,
  theme: PropTypes.oneOf(['dark', 'light']),
  disabled: PropTypes.bool,
  collapsed: PropTypes.bool,
};

NavigationGroup.propTypes = {
  mode: PropTypes.string,
  theme: PropTypes.oneOf(['dark', 'light']),
};

NavigationMenu.defaultProps = {
  mode: 'inline',
  theme: 'dark',
};

NavigationGroup.defaultProps = {
  mode: 'inline'
};

export default memo(NavigationMenu);
